// trace-viewer/src/editor/TriplanarMaterial.ts
//
// EDITOR-ONLY CODE — the live preview shader. The runtime viewer does not
// support triplanar at all: a published scene is vanilla glTF whose UVs the
// export bake (stack PR #677) produces, and the runtime loader reads nothing
// from here. The
// policy side — WHICH materials project, at what tuning — lives in
// export/triplanarPolicy.ts (shared with the bake, no shader); this module
// only renders that policy live. Consumed by exactly one caller: the editor's
// WYSIWYG preview (editor/TriplanarPreview.ts, the MaterialPreviews
// precedent). No behavior may import it; export code reads live plugin state
// through triplanarPolicy's getLiveTriplanar instead of this class.
import { Matrix } from "@babylonjs/core/Maths/math.vector";
import { MaterialPluginBase } from "@babylonjs/core/Materials/materialPluginBase";
import { RegisterClass } from "@babylonjs/core/Misc/typeStore";
import type { PBRMaterial } from "@babylonjs/core/Materials/PBR/pbrMaterial";
import type { Material } from "@babylonjs/core/Materials/material";
import type { MaterialDefines } from "@babylonjs/core/Materials/materialDefines";
import type { UniformBuffer } from "@babylonjs/core/Materials/uniformBuffer";
import type { BaseTexture } from "@babylonjs/core/Materials/Textures/baseTexture";
import type { Texture } from "@babylonjs/core/Materials/Textures/texture";
import type { AbstractEngine } from "@babylonjs/core/Engines/abstractEngine";
import type { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import type { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import type { SubMesh } from "@babylonjs/core/Meshes/subMesh";
import type { Scene } from "@babylonjs/core/scene";

import { TRIPLANAR_PLUGIN_NAME, projectionFrameInverseToRef } from "../export/triplanarPolicy";
import type { TriplanarConfig } from "../export/triplanarPolicy";

/**
 * Triplanar texture projection for PBR materials — ported from the AILayout
 * prototype's `TriplanarPBRPlugin`, then fixed in two ways the prototype
 * never was:
 *
 * - **Anchored, not world-locked.** The prototype samples `vPositionW`
 *   directly, so moving or rotating a component makes the texture swim
 *   through it. Here every sample is taken in the anchor node's rigid frame
 *   (rotation + translation removed, metric preserved): the pattern travels
 *   and turns with the component, while a morph-driven or overflow resize
 *   still re-tiles instead of smearing — vertices move through the
 *   projection field either way.
 *
 * - **The whole surface, not just its color.** The prototype projects albedo
 *   and normal but leaves the metallic/roughness (ORM) texture on UVs, so a
 *   stretched brick keeps its color pattern but its *shine* smears. The ORM
 *   texture is projected here too, AO channel included.
 *
 * Why projection at all: a smart component resizes by driving morph targets,
 * which moves vertices but leaves UVs where the artist put them, so a
 * UV-mapped texture stretches along whichever axis grew. Sampling by
 * position instead makes the pattern independent of the deformation.
 *
 * Which materials project: decided by export/triplanarPolicy.ts (asset tag →
 * textured-material heuristic with family tuning), shared verbatim with the
 * export bake so preview and bake cannot disagree. The live shader here is
 * the editor preview of the cross-platform strategy: the export-side bake
 * (TraceExport's planProjections, landing in stack PR #677) bakes the same
 * mapping into real UVs, so native viewers see what the editor shows once
 * the stack is complete — #680 must not ship a release without #677.
 *
 * Texture Scale (Renderer's `texture_transform.scale`, which lands as each
 * texture's `uScale`/`vScale`): a projected sample skips the texture matrix,
 * which is where a plain PBR shader applies that scale, so the plugin
 * multiplies each map's own scale into its sampling coordinates itself —
 * `pos * tileSize * (uScale, vScale)`, read at every bind. The published file
 * reaches the same number by the other route: the bake writes UVs at
 * `pos * tileSize` and the glTF exporter writes the texture's scale as
 * `KHR_texture_transform.scale` on top. The scale is therefore applied ONCE on
 * each side, and must never be folded into the bake's UVs as well (pinned by
 * texture-scale-export.test.ts).
 *
 * Only fragment-stage sampling is touched. PBRMaterial's vertex shader
 * already handles morph targets, so the deformation itself is unaffected.
 */

// bindForSubMesh scratch — per-frame, never escapes this module.
const TMP_ANCHOR = Matrix.Identity();
const TMP_INV_ANCHOR = Matrix.Identity();

export class TriplanarPBRPlugin extends MaterialPluginBase {
    /** Texture repetitions per meter. */
    tileSize = 1.0;
    /** Blend exponent between the three axis projections. */
    sharpness = 4.0;
    /** Albedo texture to project — null when the material has none (a bump- or ORM-only material still projects its other maps). */
    texture: Texture | null = null;
    /** Optional normal map, projected and re-basised per axis. */
    normalTexture: Texture | null = null;
    /** Optional metallic/roughness (ORM) texture — projected so shine re-tiles too. */
    ormTexture: Texture | null = null;
    /**
     * Node whose rigid frame (rotation + translation, scale excluded) anchors
     * the projection — normally the owning scene object's transform, so all of
     * a component's meshes share one continuous pattern. Falls back to the
     * rendered mesh itself when unset or disposed.
     */
    anchorNode: TransformNode | null = null;

    /**
     * The node an entrance or exit moves between the anchor and the mesh
     * (SceneObject.animationRoot), read at each bind because the sequence
     * creates it lazily. Its motion is undone before sampling, so the pattern
     * stays painted on through the clip (projectionFrameInverseToRef).
     */
    animationRoot: (() => TransformNode | null) | null = null;

    private _isEnabled = false;

    get isEnabled(): boolean { return this._isEnabled; }

    set isEnabled(value: boolean) {
        if (this._isEnabled === value) return;
        this._isEnabled = value;
        this.markAllDefinesAsDirty();
        this._enable(value);
    }

    constructor(material: PBRMaterial) {
        // 200 — after the built-in PBR blocks, before final composition.
        //
        // EVERY define the custom code branches on must be declared here:
        // Babylon collects a plugin's define NAMES from this object alone
        // (`collectDefines` reads the ctor param, and MaterialDefines emits
        // only collected keys into the shader preprocessor string), so a
        // define set solely in prepareDefines never reaches the shader and
        // its #ifdef block is silently dead code.
        super(material, TRIPLANAR_PLUGIN_NAME, 200, {
            TRIPLANAR_PBR_PLUGIN: false,
            TRIPLANAR_PBR_PLUGIN_HAS_ALBEDO: false,
            TRIPLANAR_PBR_PLUGIN_HAS_NORMAL: false,
            TRIPLANAR_PBR_PLUGIN_HAS_ORM: false,
        });
        this._enable(false);
    }

    override getClassName(): string { return "TriplanarPBRPlugin"; }

    /**
     * `Material.clone()` recreates plugins through
     * `Tools.Instantiate("BABYLON." + className)` — an unregistered class
     * makes every clone of a projected material THROW inside
     * `_ParsePlugins`. Registration keeps cloning alive; the recreated
     * plugin still carries default settings (no `@serialize` props), so
     * cloning call sites re-apply values via {@link applyTriplanar}.
     */
    static { RegisterClass("BABYLON.TriplanarPBRPlugin", TriplanarPBRPlugin); }

    override prepareDefines(defines: MaterialDefines, _scene: Scene, _mesh: AbstractMesh): void {
        defines["TRIPLANAR_PBR_PLUGIN"] = this._isEnabled;
        defines["TRIPLANAR_PBR_PLUGIN_HAS_ALBEDO"] = this._isEnabled && this.texture !== null;
        defines["TRIPLANAR_PBR_PLUGIN_HAS_NORMAL"] = this._isEnabled && this.normalTexture !== null;
        defines["TRIPLANAR_PBR_PLUGIN_HAS_ORM"] = this._isEnabled && this.ormTexture !== null;
    }

    override getUniforms(): { ubo: { name: string; size: number; type: string }[]; fragment: string } {
        return {
            ubo: [
                { name: "triTileSize", size: 1, type: "float" },
                { name: "triSharpness", size: 1, type: "float" },
                { name: "triAnchor", size: 16, type: "mat4" },
                { name: "triInvAnchor", size: 16, type: "mat4" },
                { name: "triAlbedoScale", size: 2, type: "vec2" },
                { name: "triNormalScale", size: 2, type: "vec2" },
                { name: "triOrmScale", size: 2, type: "vec2" },
            ],
            fragment: `
                #ifdef TRIPLANAR_PBR_PLUGIN
                    uniform float triTileSize;
                    uniform float triSharpness;
                    uniform mat4 triAnchor;
                    uniform mat4 triInvAnchor;
                    uniform vec2 triAlbedoScale;
                    uniform vec2 triNormalScale;
                    uniform vec2 triOrmScale;
                #endif
            `,
        };
    }

    override getSamplers(samplers: string[]): void {
        samplers.push("triAlbedoSampler");
        samplers.push("triNormalSampler");
        samplers.push("triOrmSampler");
    }

    override bindForSubMesh(
        uniformBuffer: UniformBuffer,
        _scene: Scene,
        _engine: AbstractEngine,
        subMesh: SubMesh,
    ): void {
        if (!this._isEnabled) return;
        uniformBuffer.updateFloat("triTileSize", this.tileSize);
        uniformBuffer.updateFloat("triSharpness", this.sharpness);

        // Rigid frame of the anchor — the shared helper the export bake's
        // projMatrix uses too, so preview sampling and baked UVs agree by
        // construction (see rigidFrameInverseToRef).
        const anchor = this.anchorNode && !this.anchorNode.isDisposed()
            ? this.anchorNode
            : subMesh.getMesh();
        projectionFrameInverseToRef(anchor, this.animationRoot?.() ?? null, TMP_INV_ANCHOR, TMP_ANCHOR);
        uniformBuffer.updateMatrix("triAnchor", TMP_ANCHOR);
        uniformBuffer.updateMatrix("triInvAnchor", TMP_INV_ANCHOR);

        // Each map is sampled at `pos * tileSize * (uScale, vScale)` of ITS OWN
        // texture, read here at every bind — Renderer.applyUvScale (the Texture
        // Scale slider) writes the textures directly and nothing has to tell
        // this plugin, so albedo, normal and ORM re-tile together the moment
        // the value moves. The plain PBR shader gets the same factor from the
        // texture matrix, which a projected sample bypasses.
        if (this.texture) {
            uniformBuffer.setTexture("triAlbedoSampler", this.texture);
            uniformBuffer.updateFloat2("triAlbedoScale", this.texture.uScale, this.texture.vScale);
        }
        if (this.normalTexture) {
            uniformBuffer.setTexture("triNormalSampler", this.normalTexture);
            uniformBuffer.updateFloat2("triNormalScale", this.normalTexture.uScale, this.normalTexture.vScale);
        }
        if (this.ormTexture) {
            uniformBuffer.setTexture("triOrmSampler", this.ormTexture);
            uniformBuffer.updateFloat2("triOrmScale", this.ormTexture.uScale, this.ormTexture.vScale);
        }
    }

    override isReadyForSubMesh(): boolean {
        if (!this._isEnabled) return true;
        // Follows any in-place swap of the material's metallic texture, not
        // only the Renderer's (it swaps one while a factor override drives):
        // no material-change observer fires for it. A texture the material
        // dropped is applyTriplanar's to take off.
        const orm = (this._material as PBRMaterial).metallicTexture as Texture | null;
        if (orm && this.ormTexture && orm !== this.ormTexture) this.ormTexture = orm;
        if (this.texture && !this.texture.isReady()) return false;
        if (this.normalTexture && !this.normalTexture.isReady()) return false;
        if (this.ormTexture && !this.ormTexture.isReady()) return false;
        return true;
    }

    override hasTexture(texture: BaseTexture): boolean {
        return this.texture === texture || this.normalTexture === texture || this.ormTexture === texture;
    }

    override getActiveTextures(activeTextures: BaseTexture[]): void {
        if (!this._isEnabled) return;
        if (this.texture) activeTextures.push(this.texture);
        if (this.normalTexture) activeTextures.push(this.normalTexture);
        if (this.ormTexture) activeTextures.push(this.ormTexture);
    }

    override getCustomCode(shaderType: string): Record<string, string> | null {
        if (shaderType !== "fragment") return null;

        return {
            CUSTOM_FRAGMENT_DEFINITIONS: `
                #ifdef TRIPLANAR_PBR_PLUGIN
                    // Fragment position/normal in the anchor's rigid frame —
                    // pinned to the component, world metric preserved.
                    vec3 triAnchoredPos() {
                        return (triInvAnchor * vec4(vPositionW, 1.0)).xyz;
                    }
                    vec3 triAnchoredNorm() {
                        return normalize((triInvAnchor * vec4(vNormalW, 0.0)).xyz);
                    }

                    // 'scale' is the sampled texture's own (uScale, vScale), applied
                    // to (u, v) — the plane's two axes in the order it samples
                    // them — exactly as KHR_texture_transform.scale applies to the
                    // baked UVs in the published file.
                    vec4 triplanarSample(sampler2D tex, vec3 pos, vec3 norm, float tile, vec2 scale, float sharp) {
                        vec3 blend = pow(abs(norm), vec3(sharp));
                        float blendSum = blend.x + blend.y + blend.z;
                        if (blendSum < 0.001) blendSum = 1.0;
                        blend /= blendSum;

                        vec2 uvScale = tile * scale;
                        vec4 xProj = texture2D(tex, pos.yz * uvScale);
                        vec4 yProj = texture2D(tex, pos.xz * uvScale);
                        vec4 zProj = texture2D(tex, pos.xy * uvScale);

                        return xProj * blend.x + yProj * blend.y + zProj * blend.z;
                    }

                    #ifdef TRIPLANAR_PBR_PLUGIN_HAS_ALBEDO
                        uniform sampler2D triAlbedoSampler;
                    #endif

                    #ifdef TRIPLANAR_PBR_PLUGIN_HAS_NORMAL
                        uniform sampler2D triNormalSampler;

                        // Perturbed normal in the ANCHOR frame; caller maps it
                        // back to world with triAnchor.
                        vec3 triplanarNormal(sampler2D tex, vec3 pos, vec3 norm, float tile, vec2 scale, float sharp) {
                            vec3 blend = pow(abs(norm), vec3(sharp));
                            float blendSum = blend.x + blend.y + blend.z;
                            if (blendSum < 0.001) blendSum = 1.0;
                            blend /= blendSum;

                            vec2 uvScale = tile * scale;
                            vec3 xNorm = texture2D(tex, pos.yz * uvScale).rgb * 2.0 - 1.0;
                            vec3 yNorm = texture2D(tex, pos.xz * uvScale).rgb * 2.0 - 1.0;
                            vec3 zNorm = texture2D(tex, pos.xy * uvScale).rgb * 2.0 - 1.0;

                            // Re-basis each tangent-space normal onto its
                            // projection plane's sampled axes: plane P reads
                            // uv from the two axes != P (X: yz, Y: xz, Z: xy),
                            // so n.x lands on the u-axis and n.y on -v.
                            vec3 axisX = vec3(0.0, xNorm.x, -xNorm.y);
                            vec3 axisY = vec3(yNorm.x, 0.0, -yNorm.y);
                            vec3 axisZ = vec3(zNorm.x, -zNorm.y, 0.0);

                            return normalize(axisX * blend.x + axisY * blend.y + axisZ * blend.z + norm);
                        }
                    #endif

                    #ifdef TRIPLANAR_PBR_PLUGIN_HAS_ORM
                        uniform sampler2D triOrmSampler;
                    #endif
                #endif
            `,

            // surfaceAlbedo is vAlbedoColor.rgb * <uv sample> at this point;
            // swap the UV contribution for the projection, keeping the tint.
            // Gated on HAS_ALBEDO, not the plugin define: a bump- or ORM-only
            // material has no albedo texture to project, and its surface must
            // keep the material colour while the blocks below still re-tile
            // the maps it does carry.
            CUSTOM_FRAGMENT_UPDATE_ALBEDO: `
                #ifdef TRIPLANAR_PBR_PLUGIN_HAS_ALBEDO
                    vec4 triColor = triplanarSample(triAlbedoSampler, triAnchoredPos(), triAnchoredNorm(), triTileSize, triAlbedoScale, triSharpness);
                    surfaceAlbedo = triColor.rgb * vAlbedoColor.rgb;
                #endif
            `,

            // Runs inside reflectivityBlock, after the UV texel was folded into
            // metallicRoughness — recompute both channels from the base factors
            // (reflectivityColor.rg) times the projected texel, honoring the
            // material's channel layout, and redo the AO mix from the same
            // sample. Without this the color re-tiles but the shine still
            // smears — the projection has to cover the whole surface response.
            CUSTOM_FRAGMENT_UPDATE_METALLICROUGHNESS: `
                #ifdef TRIPLANAR_PBR_PLUGIN_HAS_ORM
                    vec4 triOrm = triplanarSample(triOrmSampler, triAnchoredPos(), triAnchoredNorm(), triTileSize, triOrmScale, triSharpness);
                    vec2 triMR = reflectivityColor.rg;
                    #ifdef METALLNESSSTOREINMETALMAPBLUE
                        triMR.r *= triOrm.b;
                    #else
                        triMR.r *= triOrm.r;
                    #endif
                    #ifdef ROUGHNESSSTOREINMETALMAPALPHA
                        triMR.g *= triOrm.a;
                    #elif defined(ROUGHNESSSTOREINMETALMAPGREEN)
                        triMR.g *= triOrm.g;
                    #endif
                    metallicRoughness = triMR;
                    #ifdef AOSTOREINMETALMAPRED
                        outParams.ambientOcclusionColor = mix(ambientOcclusionColorIn, vec3(triOrm.r), reflectivityInfos.z);
                    #endif
                #endif
            `,

            CUSTOM_FRAGMENT_BEFORE_LIGHTS: `
                #ifdef TRIPLANAR_PBR_PLUGIN_HAS_NORMAL
                    vec3 triAnchorN = triplanarNormal(triNormalSampler, triAnchoredPos(), triAnchoredNorm(), triTileSize, triNormalScale, triSharpness);
                    normalW = normalize((triAnchor * vec4(triAnchorN, 0.0)).xyz);
                #endif
            `,
        };
    }

    override dispose(): void {
        // Same release set as disableTriplanar: the material owns its
        // textures independently, so this frees no GPU memory — it keeps a
        // disposed plugin from being the last thing holding a ref to a
        // texture the material has already dropped.
        this.anchorNode = null;
        this.texture = null;
        this.normalTexture = null;
        this.ormTexture = null;
        super.dispose();
    }
}

/**
 * The triplanar plugin attached to a material, if any — resolved through
 * Babylon's own per-material plugin manager (the registry `Material.clone()`
 * populates too, so a `_ParsePlugins`-recreated instance is found just like
 * a directly-applied one; a side WeakMap would miss it).
 */
export function getTriplanarPlugin(material: Material | null | undefined): TriplanarPBRPlugin | null {
    return material?.pluginManager?.getPlugin<TriplanarPBRPlugin>(TRIPLANAR_PLUGIN_NAME) ?? null;
}

/**
 * Project `material` triplanarly using its own albedo/bump/ORM textures,
 * anchored to `anchor`'s rigid frame (pass the owning scene object's
 * transform so the pattern moves with the component; omitted, each mesh
 * anchors to itself).
 *
 * When the material carries NO projectable texture — with nothing sampled,
 * nothing can smear — this DISABLES any projection an earlier apply left
 * behind (see {@link disableTriplanar}) and installs nothing new: an enabled
 * plugin holding refs to textures the material has since lost pins
 * isReadyForSubMesh false and the mesh stops rendering. Any single map
 * qualifies on its own: a flat colour cannot stretch, but a flat colour WITH
 * a bump (or ORM) map can — relief and shine smear exactly like an albedo
 * pattern would. The shader projects whichever of the three maps exist and
 * compiles out the blocks for the missing ones (HAS_ALBEDO/HAS_NORMAL/
 * HAS_ORM defines), so an albedo-less surface keeps its material colour.
 * Idempotent: re-applying updates the existing plugin rather than stacking
 * another.
 */
export function applyTriplanar(
    material: PBRMaterial,
    config: TriplanarConfig,
    anchor?: TransformNode | null,
    animationRoot?: (() => TransformNode | null) | null,
): void {
    const albedo = material.albedoTexture as Texture | null;
    const bump = material.bumpTexture as Texture | null;
    const orm = material.metallicTexture as Texture | null;
    if (!albedo && !bump && !orm) {
        disableTriplanar(material);
        return;
    }

    const existing = getTriplanarPlugin(material);
    const plugin = existing ?? new TriplanarPBRPlugin(material);
    plugin.texture = albedo;
    plugin.normalTexture = bump;
    plugin.ormTexture = orm;
    plugin.anchorNode = anchor ?? null;
    plugin.animationRoot = animationRoot ?? null;
    plugin.tileSize = config.tileSize;
    plugin.sharpness = config.sharpness;
    plugin.isEnabled = true;
    // Re-apply may flip any of albedo/normal/ORM between null and non-null;
    // the enabled flag hasn't changed then, so its setter won't dirty the
    // HAS_* defines.
    if (existing) plugin.markAllDefinesAsDirty();
}

/**
 * Take an existing projection OFF `material` in place: disable the plugin,
 * release its anchor, and drop its texture refs (a disabled plugin ignores
 * them, but holding disposed textures serves nothing). The convergence path
 * for materials this module must not dispose — adopted foreign clones — and
 * for {@link applyTriplanar}'s no-texture case. A material without a plugin
 * is left untouched; a later applyTriplanar re-enables with fresh state.
 */
export function disableTriplanar(material: Material | null | undefined): void {
    const plugin = getTriplanarPlugin(material);
    if (!plugin) return;
    plugin.isEnabled = false;
    plugin.anchorNode = null;
    plugin.animationRoot = null;
    plugin.texture = null;
    plugin.normalTexture = null;
    plugin.ormTexture = null;
}
